import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { RotateCw, ChevronLeft, ChevronRight, ArrowDown, ArrowLeft, Trophy, Loader } from "lucide-react";

const COLS = 10;
const ROWS = 20;
const SZ = 28;
const W = COLS * SZ;
const H = ROWS * SZ;
const SPEEDS = [800, 700, 600, 500, 400, 300, 250, 200, 150, 100];
const SCORE_TABLE = [0, 100, 300, 500, 800];
const API = "https://holzbuddy.team/highscore.php";

const PIECES = [
  { color: "#C4843D", shape: [[0,0,0,0],[1,1,1,1],[0,0,0,0],[0,0,0,0]] },
  { color: "#8B5E3C", shape: [[1,1],[1,1]] },
  { color: "#D4A853", shape: [[0,1,0],[1,1,1],[0,0,0]] },
  { color: "#A0694F", shape: [[0,1,1],[1,1,0],[0,0,0]] },
  { color: "#6B3E26", shape: [[1,1,0],[0,1,1],[0,0,0]] },
  { color: "#E8C07D", shape: [[1,0,0],[1,1,1],[0,0,0]] },
  { color: "#F5C800", shape: [[0,0,1],[1,1,1],[0,0,0]] },
];

// ── Cookie helpers ────────────────────────────────────────────────────────────
function getCookie(name) {
  return decodeURIComponent(
    document.cookie.split("; ").find(r => r.startsWith(name + "="))?.split("=")[1] || ""
  );
}
function setCookie(name, value, days = 365) {
  const d = new Date();
  d.setTime(d.getTime() + days * 86400000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

// ── Game helpers ──────────────────────────────────────────────────────────────
function rotateCW(m) {
  const rows = m.length, cols = m[0].length;
  const r = Array.from({ length: cols }, () => Array(rows).fill(0));
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++)
      r[x][rows - 1 - y] = m[y][x];
  return r;
}
function mkBoard() { return Array.from({ length: ROWS }, () => Array(COLS).fill(null)); }
function mkPiece() {
  const p = PIECES[Math.floor(Math.random() * PIECES.length)];
  return { shape: p.shape.map(r => [...r]), color: p.color, x: Math.floor((COLS - p.shape[0].length) / 2), y: 0 };
}
function collides(board, piece, dx = 0, dy = 0, shape = null) {
  const s = shape || piece.shape;
  for (let r = 0; r < s.length; r++)
    for (let c = 0; c < s[r].length; c++) {
      if (!s[r][c]) continue;
      const nx = piece.x + c + dx, ny = piece.y + r + dy;
      if (nx < 0 || nx >= COLS || ny >= ROWS) return true;
      if (ny >= 0 && board[ny][nx]) return true;
    }
  return false;
}

function drawCell(ctx, col, row, color, sz = SZ) {
  const x = col * sz, y = row * sz;
  ctx.fillStyle = color;
  ctx.fillRect(x + 1, y + 1, sz - 2, sz - 2);
  ctx.fillStyle = "rgba(255,255,255,0.22)";
  ctx.fillRect(x + 1, y + 1, sz - 2, 3);
  ctx.fillRect(x + 1, y + 1, 3, sz - 2);
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.fillRect(x + 1, y + sz - 4, sz - 2, 3);
  ctx.fillRect(x + sz - 4, y + 1, 3, sz - 2);
  if (sz >= 26) {
    ctx.strokeStyle = "rgba(0,0,0,0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x + 5, y + 6); ctx.lineTo(x + sz - 5, y + 6); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x + 4, y + sz / 2); ctx.lineTo(x + sz - 4, y + sz / 2); ctx.stroke();
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function HolzSpiel() {
  const cvs = useRef(null);
  const nxtCvs = useRef(null);
  const G = useRef({ board: mkBoard(), cur: null, next: null, score: 0, lines: 0, level: 0, running: false, over: false, tid: null });
  const [ui, setUI] = useState({ score: 0, lines: 0, level: 0, over: false, started: false });
  const [hs, setHs] = useState(() => +localStorage.getItem("hb_hs") || 0);

  // name + leaderboard state
  const [playerName, setPlayerName] = useState(() => getCookie("hb_player") || "");
  const [nameInput, setNameInput] = useState(() => getCookie("hb_player") || "");
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbLoading, setLbLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rank, setRank] = useState(null);

  const { lang } = useLanguage();
  const it = lang === "it";

  // Fetch leaderboard
  const fetchLB = useCallback(() => {
    setLbLoading(true);
    fetch(API)
      .then(r => r.json())
      .then(data => { setLeaderboard(Array.isArray(data) ? data : []); setLbLoading(false); })
      .catch(() => setLbLoading(false));
  }, []);

  useEffect(() => { fetchLB(); }, [fetchLB]);

  // Submit score
  const submitScore = useCallback(async () => {
    const name = nameInput.trim() || "Anonym";
    if (!name || ui.score < 100) return;
    setSubmitting(true);
    setCookie("hb_player", name);
    setPlayerName(name);
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, score: ui.score }),
      });
      const data = await res.json();
      if (data.success) {
        setLeaderboard(data.scores || []);
        setRank(data.rank || null);
        setSubmitted(true);
      }
    } catch (_) {}
    setSubmitting(false);
  }, [nameInput, ui.score]);

  // ── Draw ────────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = cvs.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const g = G.current;

    ctx.fillStyle = "#0b0c0e";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255,255,255,0.03)";
    ctx.lineWidth = 1;
    for (let i = 0; i <= ROWS; i++) { ctx.beginPath(); ctx.moveTo(0, i * SZ); ctx.lineTo(W, i * SZ); ctx.stroke(); }
    for (let i = 0; i <= COLS; i++) { ctx.beginPath(); ctx.moveTo(i * SZ, 0); ctx.lineTo(i * SZ, H); ctx.stroke(); }

    g.board.forEach((row, r) => row.forEach((color, c) => { if (color) drawCell(ctx, c, r, color); }));

    if (g.cur) {
      let gy = g.cur.y;
      while (!collides(g.board, g.cur, 0, gy - g.cur.y + 1)) gy++;
      g.cur.shape.forEach((row, r) => row.forEach((v, c) => {
        if (v) { ctx.fillStyle = "rgba(245,200,0,0.10)"; ctx.fillRect((g.cur.x + c) * SZ + 1, (gy + r) * SZ + 1, SZ - 2, SZ - 2); }
      }));
      g.cur.shape.forEach((row, r) => row.forEach((v, c) => { if (v) drawCell(ctx, g.cur.x + c, g.cur.y + r, g.cur.color); }));
    }

    const nc = nxtCvs.current;
    if (nc && g.next) {
      const nctx = nc.getContext("2d");
      nctx.fillStyle = "#0b0c0e"; nctx.fillRect(0, 0, nc.width, nc.height);
      const ox = Math.floor((4 - g.next.shape[0].length) / 2);
      const oy = Math.floor((4 - g.next.shape.length) / 2);
      g.next.shape.forEach((row, r) => row.forEach((v, c) => { if (v) drawCell(nctx, ox + c, oy + r, g.next.color, 22); }));
    }
  }, []);

  // ── Tick ────────────────────────────────────────────────────────────────────
  const tickRef = useRef(null);
  const doTick = useCallback(() => {
    const g = G.current;
    if (!g.running) return;
    if (collides(g.board, g.cur, 0, 1)) {
      g.cur.shape.forEach((row, r) => row.forEach((v, c) => {
        if (v && g.cur.y + r >= 0) g.board[g.cur.y + r][g.cur.x + c] = g.cur.color;
      }));
      const n = g.board.filter(row => row.every(Boolean)).length;
      g.board = [...Array.from({ length: n }, () => Array(COLS).fill(null)), ...g.board.filter(row => !row.every(Boolean))];
      g.score += SCORE_TABLE[n] * (g.level + 1);
      g.lines += n;
      g.level = Math.min(Math.floor(g.lines / 10), 9);
      g.cur = g.next;
      g.next = mkPiece();
      if (collides(g.board, g.cur, 0, 0)) {
        g.running = false; g.over = true;
        clearInterval(g.tid);
        if (g.score > hs) { setHs(g.score); localStorage.setItem("hb_hs", String(g.score)); }
        setUI(u => ({ ...u, score: g.score, over: true }));
        draw(); return;
      }
      setUI({ score: g.score, lines: g.lines, level: g.level, over: false, started: true });
    } else {
      g.cur.y++;
    }
    draw();
  }, [draw, hs]);

  useEffect(() => { tickRef.current = doTick; }, [doTick]);
  useEffect(() => {
    const g = G.current;
    if (g.running) { clearInterval(g.tid); g.tid = setInterval(() => tickRef.current(), SPEEDS[ui.level]); }
  }, [ui.level]);

  // ── Start ───────────────────────────────────────────────────────────────────
  const start = useCallback(() => {
    const g = G.current;
    clearInterval(g.tid);
    Object.assign(g, { board: mkBoard(), cur: mkPiece(), next: mkPiece(), score: 0, lines: 0, level: 0, running: true, over: false });
    g.tid = setInterval(() => tickRef.current(), SPEEDS[0]);
    setUI({ score: 0, lines: 0, level: 0, over: false, started: true });
    setSubmitted(false); setRank(null);
    draw();
  }, [draw]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const moveLeft  = useCallback(() => { const g = G.current; if (!g.running || collides(g.board, g.cur, -1, 0)) return; g.cur.x--; draw(); }, [draw]);
  const moveRight = useCallback(() => { const g = G.current; if (!g.running || collides(g.board, g.cur,  1, 0)) return; g.cur.x++; draw(); }, [draw]);
  const rotate    = useCallback(() => {
    const g = G.current; if (!g.running) return;
    const s = rotateCW(g.cur.shape);
    for (const k of [0, -1, 1, -2, 2]) { if (!collides(g.board, g.cur, k, 0, s)) { g.cur.shape = s; g.cur.x += k; break; } }
    draw();
  }, [draw]);
  const hardDrop  = useCallback(() => { const g = G.current; if (!g.running) return; while (!collides(g.board, g.cur, 0, 1)) g.cur.y++; doTick(); }, [doTick]);

  const held = useRef(null);
  const startHold = (fn) => (e) => { e.preventDefault(); fn(); const t = setTimeout(() => { held.current = { i: setInterval(fn, 70) }; }, 260); held.current = { t }; };
  const endHold   = () => { if (!held.current) return; clearTimeout(held.current.t); clearInterval(held.current.i); held.current = null; };

  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowLeft","ArrowRight","ArrowDown","ArrowUp"," ","Enter"].includes(e.key)) e.preventDefault();
      if (e.key === "ArrowLeft")  moveLeft();
      if (e.key === "ArrowRight") moveRight();
      if (e.key === "ArrowDown")  doTick();
      if (e.key === "ArrowUp" || e.key === " ") rotate();
      if (e.key === "Enter") hardDrop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [moveLeft, moveRight, doTick, rotate, hardDrop]);

  useEffect(() => { draw(); return () => clearInterval(G.current.tid); }, [draw]);

  const ctrlBtn = (extra = {}) => ({ borderRadius: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", userSelect: "none", touchAction: "none", border: "none", transition: "opacity 0.15s", ...extra });
  const mono = { fontFamily: "JetBrains Mono, monospace" };

  return (
    <div className="pt-20 md:pt-24 min-h-screen" style={{ background: "#0b0c0e" }}>
      <SEO title="Holzbuddy.tetris – Das Holzbau-Minispiel" description="Holzbuddy.tetris – spielen und in die Highscore-Liste eintragen." canonical="/spiel" />

      {/* nav */}
      <div className="container-hb py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white/40 hover:text-white text-xs tracking-widest uppercase font-semibold font-inter transition-colors">
          <ArrowLeft size={11} /> Holzbuddy.team
        </Link>
        <span style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.18)", fontFamily: "Inter, sans-serif" }}>Easter Egg</span>
      </div>

      <div className="container-hb pb-20 flex flex-col items-center gap-6">

        {/* title */}
        <h1 className="font-manrope font-extrabold text-white tracking-tight" style={{ fontSize: "clamp(36px,7vw,64px)" }}>
          Holzbuddy<span style={{ color: "#f5c800" }}>.tetris</span>
        </h1>

        {/* main layout */}
        <div className="flex flex-col xl:flex-row items-center xl:items-start justify-center gap-6 w-full">

          {/* canvas */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <canvas ref={cvs} width={W} height={H} style={{ display: "block", border: "1px solid rgba(245,200,0,0.15)" }} />

            {(!ui.started || ui.over) && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(11,12,14,0.9)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 24 }}>
                {ui.over ? (
                  <>
                    <p style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", fontFamily: "Inter,sans-serif" }}>
                      {it ? "Gioco finito" : "Spiel beendet"}
                    </p>
                    <p style={{ fontSize: 52, fontWeight: 800, color: "white", fontFamily: "Manrope,sans-serif", lineHeight: 1 }}>{ui.score}</p>

                    {/* rank badge */}
                    {rank && (
                      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#f5c800", fontFamily: "Inter,sans-serif" }}>
                        {it ? `Sei al posto ${rank}!` : `Platz ${rank} im Leaderboard!`}
                      </p>
                    )}

                    {/* name submit */}
                    {!submitted ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", maxWidth: 220 }}>
                        <input
                          type="text"
                          value={nameInput}
                          onChange={e => setNameInput(e.target.value.slice(0, 20))}
                          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submitScore(); } }}
                          placeholder={it ? "Il tuo nome" : "Dein Name"}
                          maxLength={20}
                          style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(245,200,0,0.3)", color: "white", padding: "10px 14px", fontSize: 13, fontFamily: "Inter,sans-serif", borderRadius: 0, outline: "none", width: "100%", boxSizing: "border-box" }}
                          autoFocus
                        />
                        <button
                          onClick={submitScore}
                          disabled={submitting || !nameInput.trim()}
                          style={{ background: "#f5c800", color: "#0b0c0e", padding: "10px 0", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "Inter,sans-serif", border: "none", cursor: submitting ? "wait" : "pointer", borderRadius: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: (!nameInput.trim() || submitting) ? 0.5 : 1 }}
                        >
                          {submitting ? <><Loader size={12} className="animate-spin" /> {it ? "Salvo..." : "Speichere..."}</> : (it ? "Invia score" : "Score einreichen")}
                        </button>
                      </div>
                    ) : (
                      <p style={{ fontSize: 10, color: "rgba(245,200,0,0.7)", fontFamily: "Inter,sans-serif", letterSpacing: "0.1em" }}>
                        ✓ {it ? "Salvato!" : "Gespeichert!"}
                      </p>
                    )}

                    <button onClick={start} style={{ marginTop: 4, background: "transparent", color: "rgba(255,255,255,0.35)", padding: "8px 24px", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "Inter,sans-serif", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer", borderRadius: 0 }}>
                      {it ? "Ancora" : "Nochmal"}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={start} style={{ background: "#f5c800", color: "#0b0c0e", padding: "13px 36px", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", fontFamily: "Inter,sans-serif", border: "none", cursor: "pointer", borderRadius: 0 }}>
                      {it ? "Inizia" : "Spielen"}
                    </button>
                    <p style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.2)", fontFamily: "Inter,sans-serif" }}>
                      {it ? "Tasto su = ruota · Invio = giù" : "Pfeiltasten · Enter = Fallen lassen"}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* right column: stats + next + leaderboard */}
          <div className="flex flex-row xl:flex-col gap-4 items-start flex-wrap justify-center xl:justify-start">

            {/* stats */}
            <div className="space-y-2">
              {[[it ? "Punti" : "Punkte", ui.score],[it ? "Linee" : "Linien", ui.lines],["Level", ui.level + 1],["Best", hs]].map(([l, v]) => (
                <div key={l} style={{ background: "rgba(255,255,255,0.05)", padding: "10px 18px", minWidth: 108 }}>
                  <p style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", fontFamily: "Inter,sans-serif", marginBottom: 4 }}>{l}</p>
                  <p style={{ fontSize: 26, fontWeight: 800, color: "white", fontFamily: "Manrope,sans-serif", lineHeight: 1 }}>{v}</p>
                </div>
              ))}
            </div>

            {/* next piece */}
            <div>
              <p style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", fontFamily: "Inter,sans-serif", marginBottom: 8 }}>
                {it ? "Prossimo" : "Nächstes"}
              </p>
              <canvas ref={nxtCvs} width={4 * 22} height={4 * 22} style={{ display: "block", border: "1px solid rgba(255,255,255,0.06)" }} />
            </div>

            {/* leaderboard */}
            <div style={{ minWidth: 180 }}>
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={11} style={{ color: "#f5c800" }} />
                <p style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", fontFamily: "Inter,sans-serif" }}>
                  {it ? "Classifica" : "Leaderboard"}
                </p>
              </div>
              {lbLoading ? (
                <div className="flex items-center gap-2" style={{ color: "rgba(255,255,255,0.2)" }}>
                  <Loader size={11} className="animate-spin" />
                  <span style={{ fontSize: 10, fontFamily: "Inter,sans-serif" }}>{it ? "Carico..." : "Lade..."}</span>
                </div>
              ) : leaderboard.length === 0 ? (
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", fontFamily: "Inter,sans-serif" }}>
                  {it ? "Ancora nessuno" : "Noch keine Einträge"}
                </p>
              ) : (
                <div className="space-y-1">
                  {leaderboard.map((entry, i) => (
                    <div key={i} className="flex items-center gap-2" style={{ padding: "5px 8px", background: i === 0 ? "rgba(245,200,0,0.08)" : "rgba(255,255,255,0.03)" }}>
                      <span style={{ ...mono, fontSize: 10, color: i === 0 ? "#f5c800" : "rgba(255,255,255,0.25)", minWidth: 16, textAlign: "right" }}>
                        {i + 1}.
                      </span>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", fontFamily: "Inter,sans-serif", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {entry.name}
                      </span>
                      <span style={{ ...mono, fontSize: 11, fontWeight: 600, color: i === 0 ? "#f5c800" : "rgba(255,255,255,0.55)" }}>
                        {Number(entry.score).toLocaleString("de")}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* controls */}
        <div className="flex flex-col items-center gap-3 mt-1">
          <button onPointerDown={(e) => { e.preventDefault(); rotate(); }} style={ctrlBtn({ background: "#f5c800", color: "#0b0c0e", width: 58, height: 58 })} aria-label="Drehen">
            <RotateCw size={22} />
          </button>
          <div className="flex items-center gap-3">
            <button onPointerDown={startHold(moveLeft)} onPointerUp={endHold} onPointerLeave={endHold} onPointerCancel={endHold} style={ctrlBtn({ background: "rgba(255,255,255,0.07)", color: "white", width: 58, height: 58 })} aria-label="Links">
              <ChevronLeft size={28} />
            </button>
            <button onPointerDown={(e) => { e.preventDefault(); hardDrop(); }} style={ctrlBtn({ background: "rgba(255,255,255,0.07)", color: "white", width: 58, height: 58 })} aria-label="Fallen lassen">
              <ArrowDown size={22} />
            </button>
            <button onPointerDown={startHold(moveRight)} onPointerUp={endHold} onPointerLeave={endHold} onPointerCancel={endHold} style={ctrlBtn({ background: "rgba(255,255,255,0.07)", color: "white", width: 58, height: 58 })} aria-label="Rechts">
              <ChevronRight size={28} />
            </button>
          </div>
          <p style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.18)", fontFamily: "Inter,sans-serif" }}>
            {it ? "Ruota · Sinistra · Giù · Destra" : "Drehen · Links · Fallen · Rechts"}
          </p>
          {/* keyboard hint */}
          <div className="hidden md:flex items-center gap-3 mt-2 pt-4" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            {[["↑ / Space", it ? "Ruota" : "Drehen"],["← →", it ? "Muovi" : "Bewegen"],["↓", it ? "Scendi" : "Runter"],["Enter", it ? "Giù subito" : "Fallen"]].map(([key, label]) => (
              <div key={key} className="flex flex-col items-center gap-1">
                <span style={{ ...mono, background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)", fontSize: 10, fontWeight: 500, padding: "3px 8px" }}>{key}</span>
                <span style={{ fontSize: 8, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.25)", fontFamily: "Inter,sans-serif" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
